import Broadcaster from "../../../helpers/Broadcaster";

// Store for tracking active checkers by ID
const activeCheckers = new Map();

const isVideoURLValid = async (url) => {
  if (!url) return false;
  try {
    const response = await fetch(`/api/v4/proxy?url=${url}`, { method: "head" });
    return response.ok;
  } catch {
    return false;
  }
};

const updateNodeAttributes = async (editor, data, attributes) => {
  const uploader = data.uploader;
  const element = document.getElementById(uploader.id);
  if (!element) return;

  try {
    const rect = element.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const auxPos = editor.view.posAtCoords({
      left: centerX,
      top: centerY,
    });

    if (!auxPos) return;

    const theNode = editor.view.state.doc.nodeAt(auxPos.inside);
    if (theNode?.attrs.uploader?.id === uploader.id) {
      if (!editor.isEditable) {
        Broadcaster.send("new_uploadFile", document.body, {
          id: data.uploader.id,
          status: "used",
          trigger: "checkUploadStatus 2",
          space_slug: attributes.uploader.space_slug,
          new_src: uploader.urlOutput,
          new_status: "completed"
        });
      }
      const { tr } = editor.view.state;
      tr.setNodeMarkup(auxPos.inside, undefined, {
        ...attributes,
        src: uploader?.urlOutput || src,
        uploader: { ...uploader },
      });
      editor.view.dispatch(tr);
    }
  } catch (error) {
    console.log("Error updating node attributes:", error);
  }
};

const createUploadTag = (resize, attributes, type, message, bgColor = null) => {
  const uploadTag = document.createElement("span");
  uploadTag.innerHTML = message;
  uploadTag.setAttribute("id", attributes.uploader.id);

  if (type === "video") {
    uploadTag.classList.add("video-processing-tag");
    uploadTag.style.width = resize.style.width;
    uploadTag.style.textAlign = "center";
    
    if (bgColor) {
      uploadTag.style.backgroundColor = bgColor;
    }
  }

  return uploadTag;
};

const clearActiveChecker = (id) => {
  if (activeCheckers.has(id)) {
    const { interval } = activeCheckers.get(id);
    clearInterval(interval);
    activeCheckers.delete(id);
  }
};

export async function checkUploadStatus(resize, editor, getPos, attributes, type) {
  const uploaderId = attributes.uploader.id;

  // Check if a checker is already running for this ID
  if (activeCheckers.has(uploaderId)) {
    // Update tag without creating a new interval
    const existingChecker = activeCheckers.get(uploaderId);
    
    if (existingChecker.resize !== resize) {
      // Update resize container in existing checker
      existingChecker.resize = resize;
      resize.style.position = "relative";
      
      // Remove old tag if exists
      while (resize.firstChild) {
        resize.removeChild(resize.firstChild);
      }
      
      if (existingChecker.uploadTag) {
        // Add tag to new container
        resize.append(existingChecker.uploadTag);
      }
    }
    
    return;
  }
  
  resize.style.position = "relative";
  
  // Determine initial status message and tag appearance
  let uploadTag;
  let checkInterval = null;
  
  if (type !== "video") {
    uploadTag = createUploadTag(resize, attributes, type, "Uploading...");
  } else if (attributes.uploader.status.indexOf("error") >= 0) {
    uploadTag = createUploadTag(
      resize, 
      attributes, 
      type,
      "This video cannot be processed, please try again...", 
      "#e53935"
    );
  } else if (attributes.uploader.status.indexOf("zoom-pending") >= 0) {
    uploadTag = createUploadTag(
      resize, 
      attributes, 
      type,
      "Your recording will be posted here after the meeting ends."
    );
  } else {
    const isValidOriginalUrl = await isVideoURLValid(attributes.uploader.url);

    if (!isValidOriginalUrl) {
      uploadTag = createUploadTag(
        resize, 
        attributes, 
        type,
        `This video is ${attributes.uploader.status}...`
      );
    }
    
    if (attributes.uploader.url) {
      let checkCount = 0;
      
      checkInterval = setInterval(async () => {
        checkCount++;
        const videoURL = attributes.uploader.urlOutput;
        const isValid = await isVideoURLValid(videoURL);
        
        if (isValid) {
          clearInterval(checkInterval);
          clearActiveChecker(uploaderId);
          
          attributes.uploader.status = "completed";
          
          const element = document.getElementById(uploaderId);
          if (element) {
            Broadcaster.send(
              "set_uploadFile",
              element,
              {
                uploader: { ...attributes.uploader },
                src: attributes.uploader.url,
              }
            );
          }
        }
      }, 10000); // Check every 10 seconds
      
      // Store information about the current checker
      activeCheckers.set(uploaderId, { 
        interval: checkInterval, 
        uploadTag, 
        resize,
        attributes
      });
    }
  }

  console.log("uploadTag", uploadTag);
  
  if (uploadTag) {
    resize.append(uploadTag);
  }
  
  // Check if this is an already completed upload
  let uploadingStatus = attributes.uploader.status;
  
  // Handle canceled uploads
  if (!uploadingStatus && editor.isEditable && type !== "video") {
    uploadTag.innerHTML = "This upload was cancelled";
  }
  
  // Set up the broadcaster receiver with a single timeout
  setTimeout(() => {
    const element = document.getElementById(uploaderId);
    
    if (!element) return;
    
    // Unregister any existing receivers
    try {
      Broadcaster.unRegisterElement("set_uploadFile", element);
    } catch (error) {
      // Ignore errors during unregistration
    }
    
    // Register new receiver
    Broadcaster.receive("set_uploadFile", element, (event, data) => {
      if (uploaderId !== data.uploader.id) {
        return;
      }
      
      // Update node after a short delay
      setTimeout(() => {
        updateNodeAttributes(editor, data, attributes);
      }, 500);
      
      // Handle completed uploads
      if (data.uploader.status === "completed") {
        // Clear active checker if it exists
        clearActiveChecker(uploaderId);
      }
      
      // Handle errors
      if (data.uploader.status.indexOf("error") >= 0) {
        uploadTag.innerHTML = "This video cannot be processed, please try again...";
        uploadTag.style.backgroundColor = "#e53935";
      }
    });
  }, 500);
}